<template>
  <div class="product-detail-wrapper" itemscope itemtype="https://schema.org/NewsArticle">
    <div class="container">
      
      <!-- Back Button & Logo -->
      <div class="nav-header animate-fade-in">
        <div class="nav-left">
          <v-btn icon @click="goBack" class="back-btn">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="nav-title">Torna</span>
        </div>
        
        <router-link to="/" class="nav-logo-link">
          <img 
            src="https://masticando-cdn.s3.eu-west-1.amazonaws.com/logo-435x435.png" 
            alt="Masticando" 
            class="header-logo"
          />
        </router-link>
      </div>

      <!-- Title Row -->
      <div class="title-row animate-fade-in">
        <div class="info-card glass-card">
          <h1 class="product-title">{{ product.name }}</h1>
          <div class="title-underline"></div>
          <div class="product-disclaimer">{{ getTranslation(language, 'product-disclaimer') }}</div>
        </div>
      </div>

      <div class="product-detail-grid">
        <!-- Grid Item 1: Image -->
        <div class="image-column animate-fade-right">
          <div class="image-card glass-card" @click="fullscreenImage = true">
            <v-img
                :src="product?.img?.formats?.medium?.url"
                :alt="product.name"
                class="product-image"
                aspect-ratio="1"
            ></v-img>
            <div class="zoom-hint">
              <v-icon size="small">mdi-magnify-plus-outline</v-icon>
            </div>
          </div>
        </div>

        <!-- Grid Item 2: Specs -->
        <div class="specs-column animate-fade-left">
           <div class="info-card glass-card full-height">
             <h2 class="section-title">{{ getTranslation(language, 'product-specs') }}</h2>
             <div class="specs-content" v-if="product.specs" v-html="product.specs"></div>
           </div>
        </div>

        <!-- Grid Item 3: Description -->
        <div class="description-column animate-fade-up">
          <div class="info-card glass-card">
            <h2 class="section-title">{{ getTranslation(language, 'product-description') }}</h2>
            <div class="description-content" 
                v-if="product.description"
                v-html="product.description + weightDisclaimerText"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fullscreen Image Dialog -->
    <v-dialog v-model="fullscreenImage" transition="dialog-bottom-transition" fullscreen>
      <div class="fullscreen-wrapper" @click="fullscreenImage = false">
        <v-btn icon class="close-btn" @click.stop="fullscreenImage = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-img :src="product?.img?.url" contain class="fullscreen-img"></v-img>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import apiService from "@/services/ApiService";
import {getTranslation} from "@/services/stringTableService";
import {defaultMeta, updateMetaInfo} from "@/middleware/metaUpdater";

export default {
  name: 'ProductDetail',
  props: {
    id: { type: String, required: true },
    slug: { type: String, required: false }
  },
  data() {
    return {
      product: {},
      language: "IT",
      fullscreenImage: false,
      weightDisclaimerText: '<br/><p class="weight-disclaimer">⚠️ Trattandosi di un prodotto naturale può presentare leggere variazioni di peso, colore e conformazione.</p>'
    }
  },
  async beforeMount() {
    const product = await this.fetchProduct();
    updateMetaInfo(this.getMetaInfo(product));
  },
  methods: {
    getTranslation,
    goBack() {
      // Logic to go back or to home anchor
      if (window.history.length > 2) {
         this.$router.back();
      } else {
         this.$router.push('/#products-deck-id-' + this.product.id); 
      }
    },
    async fetchProduct() {
      const productId = this.$route.params.id;
      try {
          this.$store.dispatch('setLoading', true);
          const product = await apiService.getProduct(productId);
          this.product = product;
          return product;
      } catch (e) {
        console.error(e);
      } finally {
        this.$store.dispatch('setLoading', false);
      }
    },
    stripHtmlTags(html) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      return tempDiv.textContent || tempDiv.innerText || '';
    },
    getMetaInfo(product){
      const productName = product?.name ?? "";
      const productDescription = product?.name + " - " +this.stripHtmlTags(product?.abstract ?? "");
      const productUrl = "https://masticaando.com" + this.$route.fullPath;
      return {
        title: `Masticando - ${productName}`,
        description: `${productDescription}`,
        author: "masticando.com",
        robots: "index, follow",
        keywords: defaultMeta.keywords + `,${productName.toLowerCase()}`,
        properties: [
          {property: 'og:title', content: "Masticando - " + productName},
          {property: 'og:description', content: productDescription},
          {property: 'og:image', content: product?.img?.url},
          {property: 'og:url', content: productUrl},
          {property: 'og:type', content: "website"},
        ]
      };
    }
  },
  watch: {
    $route(to, from) {
      if(to.params.id !== from.params.id)
        this.fetchProduct();
    }
  }
}
</script>

<style lang="scss">
@use "../style/_variables.scss" as *;

.product-detail-wrapper {
  background-color: $background-color;
  min-height: 100dvh;
  padding: 10px 0px 40px;
  color: $text-color;
  padding: 0 1rem;
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
  }
}

// Navigation
.nav-header {
  display: flex;
  align-items: center;
  justify-content: space-between; // Space between back btn and logo
  gap: 1rem;
  margin-bottom: 2rem;
  
  .nav-left {
    display: flex;
    align-items: center;
    gap: 1rem;
    cursor: pointer;
  }
  
  .back-btn {
    background: rgba(255,255,255,0.05);
    color: white;
    &:hover { background: $primary-gradient; }
  }
  
  .nav-title {
    font-family: $header-font;
    text-transform: uppercase;
    font-weight: bold;
    color: #ccc;
    font-size: 0.9rem;
    &:hover { color: white; }
  }
  .nav-logo-link {
    display: flex;
    align-items: center;
  }
}

// Title Row
.title-row {
  margin-bottom: 1rem;

  .info-card {
    padding: 1rem;
    text-align: center;
    background: $primary-gradient;
  }

  .product-title {
    font-family: $header-font;
    font-size: 2.5rem;
    line-height: 1.1;
    margin-bottom: 1rem;
    color: white;
    text-transform: uppercase;
  }
  
  .title-underline {
    width: 60px;
    height: 4px;
    background: white;
    border-radius: 2px;
    margin: 0 auto 1.5rem; // Center it
  }
  
  .product-disclaimer {
    font-size: 1rem;
    color: #ddd;
    max-width: 800px;
    text-transform: uppercase;
    margin: 0 auto; // Center text block
  }
}

// Grid Layout
.product-detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto; // Two rows
  gap: 1rem;
  
  .image-column {
    grid-column: 1;
    grid-row: 1;
  }
  
  .specs-column {
    grid-column: 2;
    grid-row: 1;
  }
  
  .description-column {
    grid-column: 1 / -1; // Span full width
    grid-row: 2;
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    
    .image-column, .specs-column, .description-column {
      grid-column: auto;
      grid-row: auto;
    }

    .image-column { order: 1; }
    .description-column { order: 2; }
    .specs-column { order: 3; }
  }
}

// Image Card
.image-card {
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  cursor: zoom-in;
  transition: transform 0.3s;
  height: 100%;
  
  &:hover {
    transform: scale(1.02);
    .zoom-hint { opacity: 1; }
  }

  .product-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .zoom-hint {
    position: absolute;
    bottom: 15px; right: 15px;
    background: $primary-gradient;
    padding: 8px;
    border-radius: 50%;
    color: white;
    opacity: 0;
    transition: opacity 0.3s;
    box-shadow: 0 4px 10px rgba(0,0,0,0.3);
  }
}

// Info & Section Content
.info-card {
  padding: 2rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03); // Glassmorphism
  border: 1px solid rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  
  &.full-height {
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .section-title {
    font-family: $header-font;
    font-size: 1.5rem;
    color: $primary-color; // Changed from secondary color to white for better contrast with underline
    margin-bottom: 2rem;
    text-transform: uppercase;
    font-weight: 700;
    position: relative;
    padding-bottom: 15px;
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 50px;
      height: 3px;
      background: $primary-gradient;
      border-radius: 2px;
    }
  }
  
  .specs-content, .description-content {
      color: #ccc;
      font-size: 1rem;
      line-height: 1.6;
      
      ul { 
        padding-left: 1.5rem; 
        margin: 1rem 0;
      }
      
      p { margin-bottom: 1rem; }

  }

  // Mobile Padding Overlay
  @media (max-width: 600px) {
    padding: 1.5rem;
    
    .product-title { 
      font-size: 2rem; 
    }
  }
}

// Header Logo Styles
.header-logo {
  height: 60px;
  width: auto;
  transition: transform 0.3s;
  &:hover { transform: scale(1.1); }
}

// Fullscreen
.fullscreen-wrapper {
  background: rgba(0,0,0,0.95);
  width: 100%; height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  .close-btn {
    position: absolute;
    top: 20px; right: 20px;
    color: black;
    z-index: 10;
  }
  
  .fullscreen-img {
    max-height: 90vh;
    max-width: 90vw;
  }
}

// Animations
.animate-fade-in { animation: fadeIn 1s ease forwards; }
.animate-fade-right { 
  opacity: 0;
  animation: fadeRight 1s ease forwards 0.2s; 
}
.animate-fade-left {
  opacity: 0;
  animation: fadeLeft 1s ease forwards 0.4s;
}
.animate-fade-up {
  opacity: 0;
  animation: fadeUp 1s ease forwards 0.6s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeRight {
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes fadeLeft {
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}
@media print {
  /* 1. Margini generosi per dare respiro al foglio */
  @page {
    size: A4 portrait;
    margin: 15mm;
  }

  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  body, .product-detail-wrapper, .v-application {
    background-color: white !important;
    color: #333 !important; /* Grigio scuro per una lettura più elegante su carta */
    padding: 0 !important;
  }

  /* Nascondi la UI */
  .nav-header, .zoom-hint, .v-dialog, footer, #footer-app {
    display: none !important;
  }

  /* 2. Titolo: centrato, pulito, con disclaimer in corsivo */
  .title-row {
    margin-bottom: 35px !important;
    text-align: center !important;
  }

  .title-row .info-card {
    background: transparent !important;
    padding: 0 !important;
    border: none !important;
    box-shadow: none !important;
  }

  .product-title {
    color: #111 !important;
    font-size: 28pt !important;
    font-weight: bold !important;
    letter-spacing: 1px !important;
    margin-bottom: 12px !important;
  }

  .title-underline {
    height: 2px !important;
    background: #111 !important;
    width: 60px !important;
    margin: 0 auto 15px !important; /* Centra la linea */
  }

  .product-disclaimer {
    display: none;
  }

  /* 3. Griglia: Layout a due colonne con gap ampio */
  .product-detail-grid {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 35px !important;
    page-break-inside: avoid;
  }

  .image-column { grid-column: 1 !important; grid-row: 1 !important; }
  .specs-column { grid-column: 2 !important; grid-row: 1 !important; }

  .description-column {
    grid-column: 1 / -1 !important;
    grid-row: 2 !important;
    margin-top: 15px !important;
  }

  /* 4. Reset delle card: niente sfondi, niente bordi */
  .info-card, .image-card {
    background: transparent !important;
    backdrop-filter: none !important;
    box-shadow: none !important;
    border: none !important;
    padding: 0 !important;
  }

  /* Immagine con border-radius preservato */
  .image-card, .v-img {
    border-radius: 16px !important;
    overflow: hidden !important;
    page-break-inside: avoid;
  }

  /* 5. Tipografia di dettaglio e separatori */
  .section-title {
    color: #111 !important;
    font-size: 16pt !important;
    text-align: left !important;
    border-bottom: 1px solid #ddd !important; /* Linea sottile ed elegante */
    padding-bottom: 8px !important;
    margin-bottom: 15px !important;
  }

  /* Rimuove il "trattino" spesso originario delle tue section-title */
  .section-title::after {
    display: none !important;
  }

  .specs-content, .description-content {
    color: #444 !important;
    font-size: 11pt !important;
    line-height: 1.1 !important;
    .weight-disclaimer {
      display: none;
    }
    p {
      margin-bottom: 0;
    }
    h2 {
      font-size: 12pt !important;
    }
  }

  /* (Opzionale) Ottimizza le liste per le "specs" stile scheda tecnica */
  .specs-content ul {
    list-style-type: none !important;
    padding-left: 0 !important;
    margin: 0 !important;
  }

  .specs-content li {
    padding: 1px 0 !important;
    border-bottom: 1px dotted #eee !important;
  }

  .specs-content li:last-child {
    border-bottom: none !important;
  }
}
</style>
